import { isNative } from './host';
import { invertRgba, restoreQrAreas, type QrArea } from './qrPolarity';

/**
 * Printing a label on a NIIMBOT D110 over Bluetooth.
 *
 * The protocol is undocumented and reverse-engineered, the library says alpha
 * and means it, and both were proved against this workshop's actual printer
 * before any of this was written — see ADR-0003 and `spikes/niimbot-d110`. What
 * is here is that spike's sequence, unchanged, with the test drawing replaced by
 * the label the server already renders.
 *
 * **The raster comes from the server.** `/labels/preview.png` is the same
 * bitmap the PDF path prints, at the template's own dpi, and reusing it means
 * the tape and the sheet cannot drift apart: one layout, one renderer, one set
 * of templates. Drawing the label a second time in a canvas here would be a
 * second implementation of everything, differing quietly.
 *
 * **Canvas orientation is not cosmetic.** With `printDirection: 'left'` the
 * encoder reads the canvas *height* as the number of dots sent to the print
 * head and its *width* as how far the tape advances. A 40×12 mm template at
 * 203 dpi comes out 320×96 — landscape, 96 across, which is exactly the D110's
 * head. Get it the other way round and the printer emits a sliver of the left
 * edge and blank tape, which looks like a protocol fault and is not one. The
 * check before sending is there because that mistake has already been made once.
 */

export type PrinterFailure =
  | 'unsupported'
  | 'insecure'
  | 'native'
  | 'refused'
  | 'head_mismatch'
  | 'no_task_version'
  | 'unknown';

export interface PrinterInfo {
  model: string;
  headDots: number;
  dpi: number;
  density: number;
  densityMin: number;
  densityMax: number;
  firmware: string;
  charge: number | null;
}

/** Change pixels, not the printer protocol: this keeps inversion consistent
 * between Web Bluetooth and the Capacitor transport. */
export function invertPixels(pixels: Uint8ClampedArray): Uint8ClampedArray {
  return invertRgba(pixels);
}

/** Why this copy cannot talk to the printer, or nothing if it can. */
export function support(): PrinterFailure | 'ok' {
  // The application has its own way there: Android WebView has no Web Bluetooth
  // at all — no flag, no permission, absent — so inside the application the
  // transport is Android's own, through a plugin. See `niimbotNative`.
  if (isNative) return 'ok';
  if (!window.isSecureContext) return 'insecure';
  if (!('bluetooth' in navigator)) return 'unsupported';
  return 'ok';
}

interface Encoded {
  cols: number;
  rows: number;
  rowsData: unknown[];
}

interface Abstraction {
  print(version: number, image: Encoded, options: { density: number; quantity: number }): Promise<void>;
  waitUntilPrintFinished(quantity: number, poll: number): Promise<void>;
  printEnd(): Promise<void>;
}

interface Client {
  connect(): Promise<void>;
  disconnect?(): Promise<void>;
  fetchPrinterInfo(): Promise<Record<string, unknown>>;
  getModelMetadata(): {
    model: string;
    dpi: number;
    printheadPixels: number;
    densityMin: number;
    densityMax: number;
    densityDefault: number;
    printDirection: 'left' | 'top';
  } | null;
  getPrintTaskVersion(): number | undefined;
  abstraction: Abstraction;
  addEventListener(name: string, handler: (event: never) => void): void;
}

let client: Client | null = null;
let meta: ReturnType<Client['getModelMetadata']> = null;
let printerInfo: PrinterInfo | null = null;

export function connected(): boolean {
  return client !== null;
}

/** The connection belongs to the app, while label dialogs are short-lived. */
export function current(): PrinterInfo | null {
  return printerInfo;
}

/**
 * The physical stock can be slightly wider than the thermal head.  D110 rolls
 * with a 14 mm face are common: a regular D110 burns its 12 mm printable band,
 * while D110_M uses its 15 mm (120-dot) head.  Keep the bitmap inside the head;
 * the remaining stock is an intentional white margin, not an invalid label.
 */
export function printableHeightMm(physicalHeightMm: number, headDots = printerInfo?.headDots ?? 96, dpi = printerInfo?.dpi ?? 203): number {
  return Math.min(physicalHeightMm, headDots * 25.4 / dpi);
}

/** Ask for the printer and open it. The browser's own chooser appears; only a
 *  press can raise it, which is why this is never called on load. */
export async function connect(): Promise<PrinterInfo> {
  const state = support();
  if (state !== 'ok') throw new Error(state);

  // Two transports, one protocol. Which one is decided here and nowhere else:
  // everything past this line — negotiation, packets, the print task — is the
  // library's and is the same either way.
  const opened = (
    isNative
      ? new (await import('./niimbotNative')).NiimbotCapacitorClient()
      : new (await import('@mmote/niimbluelib')).NiimbotBluetoothClient()
  ) as unknown as Client;

  opened.addEventListener('disconnect', () => {
    client = null;
    meta = null;
    printerInfo = null;
  });

  try {
    await opened.connect();
  } catch {
    // Cancelled at the chooser, or the printer refused. Both read the same to
    // somebody standing there, and neither is a fault to report loudly.
    throw new Error('refused');
  }

  const info = await opened.fetchPrinterInfo();
  meta = opened.getModelMetadata();
  client = opened;

  printerInfo = {
    model: meta?.model ?? String(info.modelId ?? '?'),
    headDots: meta?.printheadPixels ?? 96,
    dpi: meta?.dpi ?? 203,
    density: meta?.densityDefault ?? 2,
    densityMin: meta?.densityMin ?? 1,
    densityMax: meta?.densityMax ?? 3,
    firmware: String(info.softwareVersion ?? '?'),
    charge: typeof info.charge === 'number' ? info.charge : null,
  };
  return printerInfo;
}

export async function disconnect(): Promise<void> {
  const open = client;
  client = null;
  meta = null;
  printerInfo = null;
  await open?.disconnect?.().catch(() => undefined);
}

/**
 * Put one rendered label on the tape.
 *
 * `bitmap` is the PNG the server rendered, already the size the template says.
 * Nothing here scales it: a raster resampled on the way to a thermal head is a
 * raster with grey in it, and a thermal head has no grey.
 */
export async function print(
  bitmap: Blob,
  options: { copies: number; density: number; inverted?: boolean; qrAreas?: QrArea[]; mediaWidthMm?: number },
): Promise<void> {
  const open = client;
  if (!open) throw new Error('refused');

  const image = await createImageBitmap(bitmap);
  const canvas = document.createElement('canvas');
  canvas.width = image.width;
  canvas.height = image.height;

  const context = canvas.getContext('2d');
  if (!context) throw new Error('unknown');
  context.imageSmoothingEnabled = false;
  context.fillStyle = '#fff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, 0);
  if (options.inverted) {
    const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
    const normal = new Uint8ClampedArray(pixels.data);
    invertPixels(pixels.data);
    if (options.qrAreas?.length && (options.mediaWidthMm || options.qrAreas.some((area) => area.unit === 'px'))) {
      restoreQrAreas(
        pixels.data,
        normal,
        canvas.width,
        canvas.height,
        options.qrAreas,
        options.mediaWidthMm ? canvas.width / options.mediaWidthMm : 1,
      );
    }
    context.putImageData(pixels, 0, 0);
  }

  // `left` makes the canvas height the print-head dimension.  The D110_M has
  // 120 dots while the familiar D110 has 96, so render a smaller template in
  // the middle of the detected head instead of rejecting it as a mismatch.
  const direction = meta?.printDirection ?? 'left';
  const head = meta?.printheadPixels ?? 96;
  if (direction === 'left') {
    if (canvas.height > head) throw new Error('head_mismatch');
    if (canvas.height < head) {
      const source = document.createElement('canvas');
      source.width = canvas.width;
      source.height = canvas.height;
      const sourceContext = source.getContext('2d');
      if (!sourceContext) throw new Error('unknown');
      sourceContext.drawImage(canvas, 0, 0);
      canvas.height = head;
      context.fillStyle = options.inverted ? '#000' : '#fff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(source, 0, Math.floor((head - source.height) / 2));
    }
  }

  const version = open.getPrintTaskVersion();
  if (version === undefined || version === null) throw new Error('no_task_version');

  const { ImageEncoder } = await import('@mmote/niimbluelib');
  const encoded = ImageEncoder.encodeCanvas(canvas, direction) as unknown as Encoded;

  // The check catches a genuinely oversized raster after the smaller one was
  // padded to the detected head above.
  if (encoded.cols !== head) throw new Error('head_mismatch');

  await open.abstraction.print(version, encoded, {
    density: options.density,
    quantity: options.copies,
  });
  await open.abstraction.waitUntilPrintFinished(options.copies, 100);
  await open.abstraction.printEnd();
}
